import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Button,
  message,
  Spin,
  Input,
  Tag,
  Typography,
  Select,
  Dropdown,
  Segmented,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  PieChartOutlined,
  FileSearchOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  FileTextOutlined,
  BankOutlined,
  StopOutlined,
  PrinterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  DownOutlined,
  TeamOutlined,
  SortAscendingOutlined,
  OrderedListOutlined,
  EyeOutlined,
  UnorderedListOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import MotionHoc from "../../../utils/MotionHoc";
import {
  HEADERS_EXPORT_BEN,
  POST_LAWYER_JOB_RECEIV,
  POST_LOAN_DB2_V2,
} from "../../API/apiUrls";
import DetailContno from "./modal/DetailContno";
import PreviewExport from "./modal/PreviewExport";
import {
  exportToExcel,
  exportToPDF,
  exportToExcelMeeting,
  exportToPDFMeeting,
  sortData,
} from "./report/ReportExport";
import {
  enrichCase,
  exportSummaryResultExcel,
} from "./report/SummaryResultExport";
import { CONTRACT_TYPES } from "../cancelHand/constant/CreateCancelHandOption";
import DateCustom from "../../../hook/DateCustom";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const { Text } = Typography;

const GOLD = "#efa420";
const UNASSIGNED = "ยังไม่มอบหมาย";
const ALL_LAWYERS = "__all__";
const ALL_ENFORCERS = "__all_enforcers__";
const CARD_STYLE = { borderRadius: 12, height: "100%" };

// ─── ประเภทวันที่สำหรับค้นหา ─────────────────────────────────────
const DATE_TYPE_OPTIONS = [
  { value: 1, label: "วันที่ฟ้อง" },
  { value: 2, label: "วันพิพากษา" },
  { value: 3, label: "วันเบลตรวจ" },
  { value: 4, label: "วันเกิดสัญญา" },
  { value: 5, label: "วันที่ทนายรับงาน" },
  { value: 6, label: "วันรับชำระ" },
  { value: 7, label: "รับงานบังคับคดี" },
];

// ─── น้ำหนักการเรียงสถานะ ─────────────────────────────────────────
const statusOrderMap = {
  ส่งฟ้อง: 1,
  เตรียมเอกสาร: 2,
  เตรียมเอกสารสำเร็จ: 3,
  เอกสารไม่ครบ: 3.1,
  สร้างคำฟ้อง: 4,
  ถอนฟ้อง: 4.1,
  อุทธรณ์: 4.2,
  รอเลขคดีดำ: 5,
  รอศาลพิพากษา: 6,
  พิพากษา: 7,
  พิพากษาทำยอม: 7.1,
  คำบังคับ: 7.2,
  คดีถึงที่สุด: 7.3,
  ตรวจคำพิพากษา: 8,
  ตรวจคำพิพากษาสำเร็จ: 8.1,
  บัญชีตีกลับ: 8.2,
  รอบัญชีตรวจ: 9,
  บัญชีตรวจสำเร็จ: 9.1,
  "รอ ceo อนุมัติ": 10,
  "ceo อนุมัติ": 10.1,
  รับงานบังคับคดี: 10.2,
  "ปิดบัญชี (สัญญาใหม่)": 11,
  ปิดบัญชี: 11.1,
};

const compareStatus = (a, b) => {
  const wA = statusOrderMap[a] ?? 99;
  const wB = statusOrderMap[b] ?? 99;
  return wA !== wB ? wA - wB : a.localeCompare(b);
};

const MEETING_STATUSES = [
  "ส่งฟ้อง",
  "เตรียมเอกสาร",
  "เตรียมเอกสารสำเร็จ",
  "เอกสารไม่ครบ",
  "สร้างคำฟ้อง",
  // "ถอนฟ้อง",
  "อุทธรณ์",
  "รอเลขคดีดำ",
  "รอศาลพิพากษา",
  // "ปิดสัญญา",
];

const GROUPS = {
  prep: (s) =>
    s.includes("สืบทรัพย์") ||
    s.includes("บอกเลิก") ||
    s === "รอดำเนินการสืบทรัพย์ " ||
    s === "งานธุรการ",

  prepLawsuit: (s) =>
    s.includes("เตรียมเอกสาร") ||
    s.includes("สร้างคำฟ้อง") ||
    s.includes("ส่งฟ้อง") ||
    s.includes("เอกสารไม่ครบ"),

  court: (s) =>
    s === "อุทธรณ์" ||
    s === "รอเลขคดีดำ" ||
    s === "รอศาลพิพากษา" ||
    s === "พิพากษา" ||
    s === "พิพากษาทำยอม" ||
    s === "คำบังคับ" ||
    s === "คดีถึงที่สุด",

  review: (s) =>
    s.includes("ceo") ||
    s.includes("บัญชี") ||
    s.includes("ตรวจ") ||
    s.includes("สัญญาใหม่") ||
    s.includes("ปิดบัญชี") ||
    s === "รับงานบังคับคดี",
};

// ─── ข้อมูลแสดงผลของแต่ละกลุ่ม (ใช้ร่วมกันทั้งการ์ด / กราฟ / ตารางทนาย) ──
const GROUP_KEYS = ["prep", "prepLawsuit", "court", "review", "other"];
const GROUP_META = {
  prep: {
    label: "สืบทรัพย์ / ส่งบอกเลิก",
    short: "เตรียมข้อมูล",
    desc: "บอกเลิก, สืบทรัพย์, ธุรการ",
    color: "#faad14",
    Icon: FileSearchOutlined,
  },
  prepLawsuit: {
    label: "เตรียมฟ้อง / เอกสาร",
    short: "เตรียมฟ้อง",
    desc: "เตรียมเอกสาร, คำฟ้อง, ส่งฟ้อง",
    color: "#722ed1",
    Icon: FileTextOutlined,
  },
  court: {
    label: "ดำเนินชั้นศาล / พิพากษา",
    short: "ชั้นศาล",
    desc: "รอคดีดำ, พิพากษา, คดีถึงที่สุด",
    color: "#f5222d",
    Icon: BankOutlined,
  },
  review: {
    label: "ตรวจสอบ / ปิดบัญชี",
    short: "ตรวจสอบ",
    desc: "ตรวจคำพิพากษา, บัญชี, CEO, ปิดบัญชี",
    color: "#52c41a",
    Icon: CheckCircleOutlined,
  },
  other: {
    label: "อื่นๆ",
    short: "อื่นๆ",
    desc: "ถอนฟ้อง, คืนงาน, จ่ายเป็นปัจจุบัน",
    color: "#8c8c8c",
    Icon: StopOutlined,
  },
};

// ─── สถานะสืบทรัพย์ย่อย (ใช้กับ "เบลส่งสืบทรัพย์") ─────────────────
const INVESTIGATE_STATUS = {
  0: { label: "ยังไม่ส่งสืบ", color: "gray" },
  1: { label: "รอส่งสืบทรัพย์", color: "orange" },
  2: { label: "ส่งสืบแล้ว", color: "blue" },
  3: { label: "เจอทรัพย์", color: "green" },
  4: { label: "ไม่เจอทรัพย์", color: "red" },
};

const REPORT_MODE_OPTIONS = [
  { label: "ทั่วไป", value: "normal" },
  { label: "ข้อมูลประชุม", value: "meeting" },
  { label: "รายงานสรุปผล", value: "summary" },
];

// ถอนฟ้อง, ปิดบัญชีแต่ไม่มีผลคำพิพากษา → other
// currentStatus 26-27 (คืนงาน, จ่ายเป็นปัจจุบัน) → other, 15-25 → review
const getStatusGroup = (statusName, item = {}) => {
  const statusCode = Number(item.currentStatus);
  if (statusName === "ถอนฟ้อง") return "other";
  if (statusName === "ปิดบัญชี" && !item.idJudge) return "other";
  if (statusCode >= 26 && statusCode <= 27) return "other";
  if (statusCode >= 15 && statusCode <= 25) return "review";
  if (GROUPS.prep(statusName)) return "prep";
  if (GROUPS.prepLawsuit(statusName)) return "prepLawsuit";
  if (GROUPS.court(statusName)) return "court";
  if (GROUPS.review(statusName)) return "review";
  return "other";
};

const getTaskStatus = (item) => {
  const statusCode = Number(item.currentStatus);
  // สถานะ 18-25: ปิดบัญชีเฉพาะเมื่อ ACTIVE === 0 และ closeContno === 1, 26-27: ใช้ NAME_MAIN_STATUS
  if (statusCode >= 18 && statusCode <= 27) {
    if (
      statusCode <= 25 &&
      Number(item.ACTIVE) === 0 &&
      Number(item.closeContno) === 1
    ) {
      return "ปิดบัญชี";
    }
    if (item.enforceReceiveDate) return "รับงานบังคับคดี";
    return item.NAME_MAIN_STATUS || `สถานะรหัส ${item.currentStatus}`;
  }
  if (!item.ACTIVE) {
    return "ปิดบัญชี";
  }
  // ทนายรับงานบังคับคดีแล้ว
  if (item.enforceReceiveDate) return "รับงานบังคับคดี";
  if (item.currentStatus === null || item.currentStatus === undefined) {
    if (item.roundInvestigate) return `เบลส่งสืบทรัพย์`;
    if (item.dateCeoInvestigate) return "เฮียส่งสืบทรัพย์";
    if (item.dateCancelContno) return "ส่งบอกเลิก";
    if (item.currentStatusAd) return "งานธุรการ";
    return "รอดำเนินการสืบทรัพย์ ";
  }
  const statusName = item.NAME_MAIN_STATUS || `สถานะรหัส ${item.currentStatus}`;
  if (item.newContno && item.closeContno === 0) {
    return "ปิดบัญชี (สัญญาใหม่)";
  }
  if (statusName.includes("ปิดบัญชี")) {
    return "ปิดบัญชี";
  }

  return statusName;
};

const getStatusColor = (status) => {
  if (!status || status.includes("เตรียมเอกสารสำเร็จ")) return "default";
  if (status.includes("สำเร็จ") || status.includes("อนุมัติ")) return "success";
  if (
    status.includes("ศาล") ||
    status.includes("พิพากษา") ||
    status.includes("บังคับ") ||
    status.includes("คดีดำ") ||
    status.includes("ถึงที่สุด")
  )
    return "processing";
  if (
    status.includes("บอกเลิก") ||
    status.includes("ไม่ครบ") ||
    status.includes("ตีกลับ") ||
    status.includes("ถอน")
  )
    return "error";
  if (status.includes("รอ") || status.includes("สืบทรัพย์")) return "warning";
  if (status.includes("เอกสาร") || status.includes("ฟ้อง")) return "geekblue";
  if (status.includes("สัญญาใหม่") || status.includes("ปิดบัญชี"))
    return "cyan";
  if (status.includes("ธุรการ")) return "magenta";
  return "default";
};

// ยังไม่มอบหมาย = ไม่มีทนาย หรืออยู่ในสถานะก่อนส่งงานให้ทนาย
// (trim เพราะชื่อสถานะบางตัวมีช่องว่างท้าย เช่น "รอดำเนินการสืบทรัพย์ ")
const UNASSIGNED_STATUSES = [
  "ปิดบัญชี",
  "เบลส่งสืบทรัพย์",
  "เฮียส่งสืบทรัพย์",
  "รอดำเนินการสืบทรัพย์",
];
const isUnassigned = (item) =>
  !item.userId ||
  UNASSIGNED_STATUSES.includes((item._status ?? getTaskStatus(item)).trim());

const lawyerKeyOf = (item) => (isUnassigned(item) ? UNASSIGNED : item.userId);

const fullNameOf = (r) => `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`;

// แปะ db2 เข้า detail ตาม CONTNO ที่ตรงกัน
const mergeDB2IntoDetail = (detailRows = [], db2Rows = []) => {
  const norm = (v) =>
    String(v ?? "")
      .trim()
      .replace(/'/g, "");

  // CONTNO ของ db2 อยู่ใน LOAN.CONTNO (เผื่อ fallback ระดับบน)
  const db2Map = new Map();
  (Array.isArray(db2Rows) ? db2Rows : []).forEach((item) => {
    const contno = norm(item?.LOAN?.CONTNO ?? item?.CONTNO);
    if (contno) db2Map.set(contno, item);
  });

  return detailRows.map((row) => {
    const db2 = db2Map.get(norm(row?.CONTNO)) || null;
    return { ...row, db2 }; // เก็บก้อน db2 ไว้เป็น field เดียว
  });
};

// ─── แถบสัดส่วนงานตามกลุ่ม (ใช้ในตารางทนาย) ──────────────────────
const GroupBar = ({ groups, total }) => (
  <div
    style={{
      display: "flex",
      height: 10,
      borderRadius: 5,
      overflow: "hidden",
      background: "#f0f0f0",
      minWidth: 90,
    }}
  >
    {GROUP_KEYS.map((k) =>
      groups[k] ? (
        <Tooltip key={k} title={`${GROUP_META[k].label}: ${groups[k]}`}>
          <div
            style={{
              width: `${(groups[k] / total) * 100}%`,
              background: GROUP_META[k].color,
            }}
          />
        </Tooltip>
      ) : null,
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────
const Main = () => {
  const ROLE_ID = parseInt(localStorage.getItem("ROLE_ID"));
  const canSelectLawyer = ROLE_ID === 1 || ROLE_ID === 2 || ROLE_ID === 5;

  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [selectedLawyerId, setSelectedLawyerId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedEnforcer, setSelectedEnforcer] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const [activeChartView, setActiveChartView] = useState("steps");
  const [isModal, setIsModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [dataRecord, setDataRecord] = useState(null);
  const [reportMode, setReportMode] = useState("normal");
  const [, convertDateThaiShort] = DateCustom();
  const [sortBy, setSortBy] = useState("contno");
  const [dateType, setDateType] = useState(1);
  const [firstDate, setFirstDate] = useState(dayjs().startOf("year"));
  const [lastDate, setLastDate] = useState(dayjs().endOf("month"));

  const isMeeting = reportMode === "meeting";
  const isSummary = reportMode === "summary";

  // ─── 1. loadMainData ─────────────────────────────────────────────
  const loadMainData = useCallback(async () => {
    if (!dateType || !firstDate || !lastDate) {
      message.warning("กรุณาเลือกประเภทวันที่ และวันที่เริ่มต้น-สิ้นสุด");
      return;
    }
    if (firstDate.isAfter(lastDate, "day")) {
      message.warning("วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด");
      return;
    }
    setLoading(true);
    const payload = {
      userId: 1,
      dateType,
      firstDate: firstDate.format("YYYY-MM-DD"),
      lastDate: lastDate.format("YYYY-MM-DD"),
    };
    try {
      const res = await axios.post(POST_LAWYER_JOB_RECEIV, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res.data?.data?.length > 0) {
        setDataArr(res.data.data);
      } else {
        setDataArr([]);
        message.info("ไม่พบข้อมูล");
      }
      setLastLoadedAt(dayjs());
    } catch (error) {
      message.error(
        error.response?.data?.message ||
          error.message ||
          "ไม่สามารถโหลดข้อมูลได้",
      );
    } finally {
      setLoading(false);
    }
  }, [dateType, firstDate, lastDate]);

  // ─── 2. คำนวณสถานะ/กลุ่มครั้งเดียวต่อแถว ──────────────────────────
  const rows = useMemo(
    () =>
      dataArr.map((item) => {
        const _status = getTaskStatus(item);
        return { ...item, _status, _group: getStatusGroup(_status, item) };
      }),
    [dataArr],
  );

  // โหมดข้อมูลประชุม: ใช้เป็นจุดตั้งต้นของทั้งหน้า
  const baseData = useMemo(
    () =>
      isMeeting
        ? rows.filter((d) => MEETING_STATUSES.includes(d._status))
        : rows,
    [rows, isMeeting],
  );

  // ─── 3. คำนวณ dashboard ──────────────────────────────────────────
  const { groupCounts, chartData, lawyerStats, lawyerComparisonData } =
    useMemo(() => {
      const counts = { total: 0 };
      GROUP_KEYS.forEach((k) => (counts[k] = 0));
      const statusMap = {};
      const lawyerMap = {};

      baseData.forEach((item) => {
        const { _status, _group } = item;
        counts.total += 1;
        counts[_group] += 1;

        if (!statusMap[_status]) {
          statusMap[_status] = { name: _status, count: 0, group: _group };
        }
        statusMap[_status].count += 1;

        const uId = lawyerKeyOf(item);
        if (!lawyerMap[uId]) {
          lawyerMap[uId] = {
            userId: uId,
            username: uId === UNASSIGNED ? UNASSIGNED : item.NNAME || `${uId}`,
            total: 0,
            groups: {},
          };
        }
        lawyerMap[uId].total += 1;
        lawyerMap[uId].groups[_group] =
          (lawyerMap[uId].groups[_group] || 0) + 1;
      });

      const lawyers = Object.values(lawyerMap).sort(
        (a, b) => b.total - a.total,
      );

      return {
        groupCounts: counts,
        chartData: Object.values(statusMap).sort((a, b) =>
          compareStatus(a.name, b.name),
        ),
        lawyerStats: lawyers,
        lawyerComparisonData: lawyers
          .map((l) => ({
            userId: l.userId,
            username: l.username,
            prepCount: l.groups.prepLawsuit || 0,
            courtCount: l.groups.court || 0,
          }))
          .filter((d) => d.prepCount + d.courtCount > 0)
          .sort(
            (a, b) => b.prepCount + b.courtCount - (a.prepCount + a.courtCount),
          ),
      };
    }, [baseData]);

  const selectedLawyerName = useMemo(() => {
    if (!selectedLawyerId) return "";
    return (
      lawyerStats.find((l) => l.userId === selectedLawyerId)?.username ||
      `${selectedLawyerId}`
    );
  }, [lawyerStats, selectedLawyerId]);

  // ─── 4. ตัวกรองตาราง ─────────────────────────────────────────────
  const lawyerFiltered = useMemo(
    () =>
      selectedLawyerId
        ? baseData.filter((d) => lawyerKeyOf(d) === selectedLawyerId)
        : baseData,
    [baseData, selectedLawyerId],
  );

  // dropdown ผู้รับงานบังคับคดี (จาก enforceReceiveByName)
  const enforcerOptions = useMemo(() => {
    const counts = {};
    lawyerFiltered.forEach((d) => {
      const name = d.enforceReceiveByName?.trim();
      if (name) counts[name] = (counts[name] || 0) + 1;
    });
    return [
      { value: ALL_ENFORCERS, label: "ผู้รับงานบังคับคดีทั้งหมด" },
      ...Object.keys(counts)
        .sort((a, b) => a.localeCompare(b, "th"))
        .map((name) => ({ value: name, label: `${name} (${counts[name]})` })),
    ];
  }, [lawyerFiltered]);

  const statusOptions = useMemo(() => {
    const source = selectedGroup
      ? lawyerFiltered.filter((d) => d._group === selectedGroup)
      : lawyerFiltered;
    return Array.from(new Set(source.map((d) => d._status)))
      .sort(compareStatus)
      .map((s) => ({ value: s, label: s }));
  }, [lawyerFiltered, selectedGroup]);

  const detailTableData = useMemo(() => {
    let filtered = lawyerFiltered;
    if (selectedGroup) {
      filtered = filtered.filter((d) => d._group === selectedGroup);
    }
    if (selectedEnforcer) {
      filtered = filtered.filter(
        (d) => d.enforceReceiveByName?.trim() === selectedEnforcer,
      );
    }
    if (selectedStatusFilter.length > 0) {
      filtered = filtered.filter((d) =>
        selectedStatusFilter.includes(d._status),
      );
    }
    const query = searchInputTable.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (item) =>
          item.CONTNO?.toLowerCase().includes(query) ||
          item.newContno?.toLowerCase().includes(query) ||
          fullNameOf(item).toLowerCase().includes(query),
      );
    }
    return sortData(filtered, sortBy);
  }, [
    lawyerFiltered,
    selectedGroup,
    selectedEnforcer,
    selectedStatusFilter,
    searchInputTable,
    sortBy,
  ]);

  // dropdown ทนาย: ทั้งหมด → ยังไม่มอบหมาย → รายชื่อทนาย (เรียงตามชื่อ)
  const lawyerOptions = useMemo(() => {
    const unassignedCount =
      lawyerStats.find((l) => l.userId === UNASSIGNED)?.total || 0;
    return [
      { value: ALL_LAWYERS, label: `ทนายทั้งหมด (${groupCounts.total})` },
      { value: UNASSIGNED, label: `งานที่ยังไม่มอบหมาย (${unassignedCount})` },
      ...lawyerStats
        .filter((l) => l.userId !== UNASSIGNED)
        .sort((a, b) => a.username.localeCompare(b.username, "th"))
        .map((l) => ({ value: l.userId, label: `${l.username} (${l.total})` })),
    ];
  }, [lawyerStats, groupCounts.total]);

  // ─── 5. Actions ของตัวกรอง ───────────────────────────────────────
  const changeLawyer = (id) => {
    if (!canSelectLawyer) return;
    setSelectedLawyerId(id);
    setSelectedEnforcer(null);
    setSelectedStatusFilter([]);
    setSearchInputTable("");
  };

  const selectLawyer = (id) =>
    changeLawyer(selectedLawyerId === id ? null : id);

  const selectGroup = (group) => {
    setSelectedGroup((prev) => (prev === group ? null : group));
    setSelectedStatusFilter([]);
  };

  const selectStatus = (status) => {
    setSelectedGroup(null);
    setSelectedStatusFilter((prev) =>
      prev.length === 1 && prev[0] === status ? [] : [status],
    );
  };

  const clearFilters = () => {
    setSelectedLawyerId(null);
    setSelectedGroup(null);
    setSelectedEnforcer(null);
    setSelectedStatusFilter([]);
    setSearchInputTable("");
  };

  const hasActiveFilter =
    !!selectedLawyerId ||
    !!selectedGroup ||
    !!selectedEnforcer ||
    selectedStatusFilter.length > 0 ||
    !!searchInputTable.trim();

  // ─── 6. ECharts options ──────────────────────────────────────────

  // 6.1 กราฟสถานะ (แท่งแนวนอน สีตามกลุ่ม, คลิกเพื่อกรอง)
  const stepsChartHeight = Math.max(360, chartData.length * 26 + 40);
  const barOptionsSteps = useMemo(
    () => ({
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      grid: { left: 8, right: 48, top: 8, bottom: 8, containLabel: true },
      xAxis: {
        type: "value",
        minInterval: 1,
        splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } },
      },
      yAxis: {
        type: "category",
        inverse: true,
        data: chartData.map((d) => d.name),
        axisLabel: { color: "#595959", fontSize: 12 },
        axisTick: { show: false },
      },
      series: [
        {
          name: "จำนวนคดี",
          type: "bar",
          barMaxWidth: 18,
          cursor: "pointer",
          data: chartData.map((d) => ({
            value: d.count,
            itemStyle: {
              color: GROUP_META[d.group].color,
              borderRadius: [0, 4, 4, 0],
              opacity:
                selectedStatusFilter.length === 0 ||
                selectedStatusFilter.includes(d.name)
                  ? 1
                  : 0.3,
            },
          })),
          label: {
            show: true,
            position: "right",
            color: "#595959",
            fontWeight: "bold",
          },
        },
      ],
    }),
    [chartData, selectedStatusFilter],
  );

  // 6.2 กราฟเทียบภาระงานทนาย (คลิกเพื่อเลือกทนาย)
  const barOptionsLawyers = useMemo(() => {
    const series = (key, field) => ({
      name: GROUP_META[key].label,
      type: "bar",
      barMaxWidth: 28,
      cursor: canSelectLawyer ? "pointer" : "default",
      data: lawyerComparisonData.map((d) => d[field]),
      itemStyle: { color: GROUP_META[key].color, borderRadius: [4, 4, 0, 0] },
      label: {
        show: true,
        position: "top",
        formatter: (p) => (p.value > 0 ? p.value : ""),
        color: "#595959",
        fontWeight: "bold",
      },
    });
    return {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { top: 0 },
      grid: { left: 8, right: 8, bottom: 8, top: 40, containLabel: true },
      xAxis: {
        type: "category",
        data: lawyerComparisonData.map((d) => d.username),
        axisLabel: { interval: 0, rotate: 30, color: "#595959", fontSize: 11 },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } },
      },
      series: [
        series("prepLawsuit", "prepCount"),
        series("court", "courtCount"),
      ],
    };
  }, [lawyerComparisonData, canSelectLawyer]);

  const chartEvents = {
    click: (p) => {
      if (activeChartView === "steps") {
        const status = chartData[p.dataIndex]?.name;
        if (status) selectStatus(status);
      } else {
        const lawyer = lawyerComparisonData[p.dataIndex];
        if (lawyer) selectLawyer(lawyer.userId);
      }
    },
  };

  // ─── 7. Columns ──────────────────────────────────────────────────
  const lawyerColumns = [
    {
      title: "ชื่อทนาย",
      dataIndex: "username",
      key: "username",
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: "งานรวม",
      dataIndex: "total",
      key: "total",
      align: "center",
      width: 80,
      render: (val) => (
        <Tag color="gold" style={{ fontWeight: "bold", marginInlineEnd: 0 }}>
          {val}
        </Tag>
      ),
    },
    {
      title: "สัดส่วนตามขั้นตอน",
      key: "groups",
      width: "40%",
      render: (_, r) => <GroupBar groups={r.groups} total={r.total} />,
    },
  ];

  const detailColumns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      render: (_, record) => (
        <Tag
          color="blue"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setDataRecord(record);
            setIsModal(true);
          }}
        >
          {record.CONTNO}
        </Tag>
      ),
    },
    {
      title: "ชื่อ-นามสกุลจำเลย",
      key: "fullName",
      render: (_, r) => fullNameOf(r),
    },
    {
      title: "ผู้รับผิดชอบ",
      dataIndex: "NNAME",
      key: "NNAME",
      align: "center",
    },
    {
      title: "สถานะงานปัจจุบัน",
      key: "status",
      align: "center",
      render: (_, record) => {
        const s = record._status;
        const investigate =
          s === "เบลส่งสืบทรัพย์"
            ? INVESTIGATE_STATUS[record.statusActiveInvestigate]
            : null;
        const tag = (
          <Tag
            color={
              s === "เบลส่งสืบทรัพย์"
                ? investigate?.color || "gray"
                : getStatusColor(s)
            }
          >
            {s}
            {investigate ? ` (${investigate.label})` : null}
          </Tag>
        );
        if (s !== "รับงานบังคับคดี") return tag;
        return (
          <Space direction="vertical" size={0}>
            {tag}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.enforceReceiveByName || "-"} (
              {convertDateThaiShort(record.enforceReceiveDate)})
            </Text>
          </Space>
        );
      },
    },
  ];

  // ─── 8. ดึงข้อมูลลูกหนี้คงเหลือ (DB2) ────────────────────────────
  const queryMultiData = useCallback(async (payload) => {
    if (!payload || Object.keys(payload).length === 0) {
      message.error("ไม่มีข้อมูลสำหรับการค้นหา");
      return [];
    }

    setLoading(true);
    try {
      const allContracts = [];
      CONTRACT_TYPES.forEach((type) => {
        if (payload[type]) {
          payload[type]
            .split(",")
            .map((c) => c.trim().replace(/'/g, ""))
            .filter(Boolean)
            .forEach((contno) => allContracts.push({ type, contno }));
        }
      });

      if (allContracts.length === 0) {
        message.warning("ไม่มีเลขสัญญาที่ต้องค้นหา");
        return [];
      }

      const chunkSize = 25;
      const chunks = [];
      for (let i = 0; i < allContracts.length; i += chunkSize) {
        chunks.push(allContracts.slice(i, i + chunkSize));
      }

      const allResults = [];
      for (let i = 0; i < chunks.length; i++) {
        const chunkPayload = CONTRACT_TYPES.reduce((acc, type) => {
          const contractsOfType = chunks[i]
            .filter((item) => item.type === type)
            .map((item) => `'${item.contno}'`);
          acc[type] = contractsOfType.length ? contractsOfType.join(",") : "";
          return acc;
        }, {});

        try {
          const response = await axios.post(POST_LOAN_DB2_V2, chunkPayload, {
            headers: HEADERS_EXPORT_BEN,
          });
          if (response.status === 200 && response.data) {
            const chunkData = Array.isArray(response.data)
              ? response.data
              : [response.data];
            allResults.push(...chunkData);
          }
        } catch (chunkError) {
          console.error(`Chunk ${i + 1} failed:`, chunkError);
        }

        if (i < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      if (allResults.length === 0) {
        message.warning("ไม่พบข้อมูลสัญญาที่ค้นหา");
      } else if (allResults.length < allContracts.length) {
        message.warning(
          `พบข้อมูล ${allResults.length} จาก ${allContracts.length} สัญญา`,
        );
      }

      return allResults;
    } catch (error) {
      console.error("Error in queryMultiData:", error);
      const status = error?.response?.status;
      message.error(
        status === 404
          ? "ไม่พบเลขที่สัญญา"
          : `เกิดข้อผิดพลาดในการดึงข้อมูล${status ? ` (${status})` : ""}`,
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDataQuery = useCallback(
    (dataRows = []) => {
      const buckets = CONTRACT_TYPES.reduce((acc, type) => {
        acc[type] = [];
        return acc;
      }, {});

      dataRows.forEach((row) => {
        const type = String(row?.SCHEMA || "").toLowerCase();
        const contno = row?.CONTNO?.trim();
        if (
          contno &&
          CONTRACT_TYPES.includes(type) &&
          !buckets[type].includes(contno)
        ) {
          buckets[type].push(contno);
        }
      });

      const payload = CONTRACT_TYPES.reduce((acc, type) => {
        acc[type] = buckets[type].length
          ? buckets[type].map((c) => `'${c}'`).join(",")
          : "";
        return acc;
      }, {});

      return queryMultiData(payload);
    },
    [queryMultiData],
  );

  // ลูกหนี้คงเหลือดึงได้เฉพาะเมื่อเลือกทนาย (ข้อมูลทั้งหมดใหญ่เกินไป)
  const canQueryDB2 = !!selectedLawyerId && selectedLawyerId !== UNASSIGNED;

  const fetchDetailWithDB2 = async () => {
    const dataDB2 = canQueryDB2 ? await handleDataQuery(detailTableData) : [];
    return mergeDB2IntoDetail(detailTableData, dataDB2);
  };

  // ✅ Preview ข้อมูลชุดเดียวกับ Excel
  const handlePreview = async () => {
    if (detailTableData.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับ preview");
      return;
    }
    setPreviewData(await fetchDetailWithDB2());
  };

  // ─── 9. Export ───────────────────────────────────────────────────
  const exportSummary = async () => {
    // ไม่เลือกทนาย -> เอาเฉพาะสัญญาที่กำหนดทนายแล้ว (ตัด ยังไม่มอบหมาย)
    const exportRows = selectedLawyerId
      ? detailTableData
      : detailTableData.filter((d) => !isUnassigned(d));
    if (exportRows.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับ export");
      return;
    }
    try {
      const dataDB2 = await handleDataQuery(exportRows);
      setLoading(true);
      const enriched = mergeDB2IntoDetail(exportRows, dataDB2).map((item) =>
        enrichCase(item, getTaskStatus),
      );
      const name = selectedLawyerName || "ทนายทั้งหมด";
      await exportSummaryResultExcel({
        rows: enriched,
        subtitle: `${name} | ข้อมูล ณ วันที่ ${dayjs().format(
          "DD/MM/YYYY HH:mm",
        )}`,
        formatDate: convertDateThaiShort,
        groupOf: getStatusGroup,
        showLawyer: !selectedLawyerId,
        fileName: `รายงานสรุปผล_${name}_${dayjs().format(
          "YYYYMMDD_HHmm",
        )}.xlsx`,
      });
    } catch (error) {
      console.error(error);
      message.error("Export Excel ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  const exportMeeting = (key) =>
    key === "pdf"
      ? exportToPDFMeeting(detailTableData, getTaskStatus, selectedLawyerName)
      : exportToExcelMeeting(detailTableData, getTaskStatus);

  const exportNormal = async (key) => {
    if (key === "pdf") {
      exportToPDF(detailTableData, getTaskStatus, selectedLawyerName);
      return;
    }
    if (!canQueryDB2) {
      message.warning(
        "ถ้าไม่เลือกทนาย จะไม่สามารถดูข้อมูลลูกหนี้คงเหลือได้ !!",
      );
    }
    exportToExcel(await fetchDetailWithDB2(), getTaskStatus);
  };

  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
        // รายงานสรุปผล export ได้แค่ excel
        disabled: isSummary,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (isSummary) return exportSummary();
      if (detailTableData.length === 0) {
        message.warning("ไม่มีข้อมูลสำหรับ export");
        return;
      }
      if (isMeeting) return exportMeeting(key);
      return exportNormal(key);
    },
  };

  // ─── 10. Render ──────────────────────────────────────────────────
  const kpiCards = [
    {
      key: null,
      label: "สัญญาทั้งหมด",
      desc: "ทุกสถานะในช่วงที่ค้นหา",
      value: groupCounts.total,
      color: "#1890ff",
      Icon: PieChartOutlined,
    },
    ...GROUP_KEYS.map((k) => ({
      key: k,
      ...GROUP_META[k],
      value: groupCounts[k],
    })),
  ];

  return (
    <div
      style={{
        backgroundColor: "#f0f2f5",
        minHeight: "100vh",
        paddingBottom: 24,
      }}
    >
      {/* Header + ตัวกรองช่วงข้อมูล */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          border: "1px solid #ffcb05",
          background: "#fffdf0",
        }}
        styles={{ body: { padding: "16px 20px" } }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Space size={8}>
            <BarChartOutlined style={{ fontSize: 20, color: GOLD }} />
            <span style={{ fontSize: 18, fontWeight: "bold", color: GOLD }}>
              ภาพรวมงานทนายความ
            </span>
          </Space>
          <Space wrap size={8}>
            <Select
              value={dateType}
              onChange={setDateType}
              options={DATE_TYPE_OPTIONS}
              style={{ width: 160 }}
            />
            <ThaiDatePicker
              value={firstDate}
              onChange={setFirstDate}
              placeholder="วันที่เริ่มต้น"
              disabledDate={(d) => lastDate && d.isAfter(lastDate, "day")}
              style={{ width: 150 }}
            />
            <Text type="secondary">ถึง</Text>
            <ThaiDatePicker
              value={lastDate}
              onChange={setLastDate}
              placeholder="วันที่สิ้นสุด"
              disabledDate={(d) => firstDate && d.isBefore(firstDate, "day")}
              style={{ width: 150 }}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={loadMainData}
              loading={loading}
              style={{ background: GOLD }}
            >
              ค้นหา
            </Button>
            {lastLoadedAt && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                อัปเดต {lastLoadedAt.format("HH:mm")}
              </Text>
            )}
          </Space>
        </div>
      </Card>

      <Spin spinning={loading} size="large" tip="กำลังโหลดและประมวลผลข้อมูล...">
        {/* Pipeline KPI: คลิกเพื่อกรองตามกลุ่ม */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {kpiCards.map((card) => {
            const active = card.key === selectedGroup;
            return (
              <Col
                key={card.key || "total"}
                xs={12}
                md={8}
                xl={4}
                style={{ display: "flex" }}
              >
                <Card
                  hoverable
                  onClick={() =>
                    card.key ? selectGroup(card.key) : setSelectedGroup(null)
                  }
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    borderTop: `4px solid ${card.color}`,
                    outline: active ? `2px solid ${card.color}` : "none",
                    background: active ? `${card.color}0d` : "#fff",
                  }}
                  styles={{ body: { padding: "12px 16px" } }}
                >
                  <Space size={6} style={{ color: "#595959" }}>
                    <card.Icon style={{ color: card.color }} />
                    <Text strong>{card.label}</Text>
                  </Space>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 600,
                      color: card.color,
                      lineHeight: 1.4,
                    }}
                  >
                    {card.value}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {card.desc}
                  </Text>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* กราฟ + สรุปรายทนาย */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={15}>
            <Card
              variant="borderless"
              style={CARD_STYLE}
              title={
                <Space>
                  <BarChartOutlined style={{ color: GOLD }} />
                  สถิติการดำเนินการ
                </Space>
              }
              extra={
                <Segmented
                  value={activeChartView}
                  onChange={setActiveChartView}
                  options={[
                    { label: "ตามสถานะ", value: "steps" },
                    { label: "เทียบภาระงานทนาย", value: "lawyers" },
                  ]}
                />
              }
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                {activeChartView === "steps"
                  ? "คลิกแท่งเพื่อกรองรายการคดีตามสถานะ"
                  : canSelectLawyer
                    ? "คลิกแท่งเพื่อดูรายการคดีของทนาย"
                    : "เทียบงานเตรียมฟ้องและชั้นศาลของแต่ละทนาย"}
              </Text>
              <ReactECharts
                key={activeChartView}
                option={
                  activeChartView === "steps"
                    ? barOptionsSteps
                    : barOptionsLawyers
                }
                onEvents={chartEvents}
                notMerge={true}
                style={{
                  height: activeChartView === "steps" ? stepsChartHeight : 400,
                  width: "100%",
                }}
              />
            </Card>
          </Col>

          <Col xs={24} lg={9}>
            <Card
              variant="borderless"
              style={CARD_STYLE}
              title={
                <Space>
                  <TeamOutlined style={{ color: "#1677ff" }} />
                  สรุปงานรายบุคคล
                </Space>
              }
            >
              {canSelectLawyer && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  คลิกชื่อทนายเพื่อดูรายการคดี
                </Text>
              )}
              <Table
                columns={lawyerColumns}
                dataSource={lawyerStats}
                rowKey="userId"
                pagination={false}
                size="small"
                scroll={{ y: 360 }}
                onRow={(record) => ({
                  onClick: () => selectLawyer(record.userId),
                  style: canSelectLawyer ? { cursor: "pointer" } : undefined,
                })}
                rowClassName={(record) =>
                  record.userId === selectedLawyerId
                    ? "ant-table-row-selected"
                    : ""
                }
              />
              <Space wrap size={[12, 4]} style={{ marginTop: 12 }}>
                {GROUP_KEYS.map((k) => (
                  <Space key={k} size={4}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: GROUP_META[k].color,
                      }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {GROUP_META[k].short}
                    </Text>
                  </Space>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>

        {/* รายการคดี */}
        <Card
          variant="borderless"
          style={{ borderRadius: 12 }}
          title={
            <Space>
              <UnorderedListOutlined style={{ color: GOLD }} />
              {selectedLawyerId
                ? `รายการคดีของ: ${selectedLawyerName}`
                : "รายการคดีทั้งหมดในระบบ"}
            </Space>
          }
          extra={
            <Space wrap size={8}>
              <Text type="secondary">รูปแบบรายงาน</Text>
              <Segmented
                value={reportMode}
                onChange={setReportMode}
                options={REPORT_MODE_OPTIONS}
              />
              <Button
                icon={<EyeOutlined />}
                onClick={handlePreview}
                loading={loading}
              >
                Preview
              </Button>
              <Dropdown menu={exportMenuItems} trigger={["click"]}>
                <Button type="primary" ghost icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          }
        >
          {/* Toolbar ตัวกรอง */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <Space wrap size={8}>
              <Select
                style={{ minWidth: 220 }}
                suffixIcon={<TeamOutlined />}
                value={selectedLawyerId || ALL_LAWYERS}
                onChange={(v) => changeLawyer(v === ALL_LAWYERS ? null : v)}
                options={lawyerOptions}
                disabled={!canSelectLawyer}
                showSearch
                optionFilterProp="label"
              />
              <Select
                style={{ minWidth: 220 }}
                suffixIcon={<BankOutlined />}
                value={selectedEnforcer || ALL_ENFORCERS}
                onChange={(v) =>
                  setSelectedEnforcer(v === ALL_ENFORCERS ? null : v)
                }
                options={enforcerOptions}
                showSearch
                optionFilterProp="label"
              />
              <Select
                style={{ minWidth: 240 }}
                placeholder="กรองตามสถานะ"
                suffixIcon={<FilterOutlined />}
                value={selectedStatusFilter}
                allowClear
                onChange={setSelectedStatusFilter}
                mode="multiple"
                maxTagCount="responsive"
                options={statusOptions}
                size="large"
              />
              <Input
                placeholder="ค้นหาเลขสัญญา, ชื่อ..."
                value={searchInputTable}
                onChange={(e) => setSearchInputTable(e.target.value)}
                style={{ width: 220 }}
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                allowClear
                size="small"
              />
            </Space>
            <Space size={8}>
              <Text type="secondary">เรียงตาม</Text>
              <Segmented
                value={sortBy}
                onChange={setSortBy}
                options={[
                  {
                    label: "เลขสัญญา",
                    value: "contno",
                    icon: <SortAscendingOutlined />,
                  },
                  {
                    label: "สถานะงาน",
                    value: "status",
                    icon: <OrderedListOutlined />,
                  },
                ]}
              />
            </Space>
          </div>

          {/* ตัวกรองที่ใช้อยู่ */}
          {(hasActiveFilter || isMeeting) && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                marginBottom: 12,
                background: "#fafafa",
                border: "1px solid #f0f0f0",
                borderRadius: 8,
              }}
            >
              <Text type="secondary" style={{ marginRight: 4 }}>
                กำลังกรอง:
              </Text>
              {isMeeting && (
                <Tag
                  color="gold"
                  closable
                  onClose={() => setReportMode("normal")}
                >
                  เฉพาะข้อมูลประชุม
                </Tag>
              )}
              {selectedLawyerId && (
                <Tag
                  color="blue"
                  closable
                  onClose={() => setSelectedLawyerId(null)}
                >
                  ทนาย: {selectedLawyerName}
                </Tag>
              )}
              {selectedEnforcer && (
                <Tag
                  color="processing"
                  closable
                  onClose={() => setSelectedEnforcer(null)}
                >
                  บังคับคดี: {selectedEnforcer}
                </Tag>
              )}
              {selectedGroup && (
                <Tag
                  color={GROUP_META[selectedGroup].color}
                  closable
                  onClose={() => setSelectedGroup(null)}
                >
                  ขั้นตอน: {GROUP_META[selectedGroup].label}
                </Tag>
              )}
              {selectedStatusFilter.map((s) => (
                <Tag
                  key={s}
                  closable
                  onClose={() =>
                    setSelectedStatusFilter((prev) =>
                      prev.filter((x) => x !== s),
                    )
                  }
                >
                  สถานะ: {s}
                </Tag>
              ))}
              {searchInputTable.trim() && (
                <Tag closable onClose={() => setSearchInputTable("")}>
                  ค้นหา: {searchInputTable.trim()}
                </Tag>
              )}
              {hasActiveFilter && (
                <Button type="link" size="small" onClick={clearFilters}>
                  ล้างทั้งหมด
                </Button>
              )}
            </div>
          )}

          <Table
            size="small"
            columns={detailColumns}
            dataSource={detailTableData}
            rowKey="loanId"
            scroll={{ x: 600 }}
            pagination={{
              defaultPageSize: 15,
              showSizeChanger: true,
              showTotal: (total) => `ทั้งหมด ${total} รายการ`,
              pageSizeOptions: ["10", "15", "20", "50"],
            }}
          />
        </Card>

        {previewData && (
          <PreviewExport
            open={!!previewData}
            close={() => setPreviewData(null)}
            data={previewData}
            getTaskStatus={getTaskStatus}
            title={selectedLawyerName || "ทนายทั้งหมด"}
          />
        )}
        {isModal && (
          <DetailContno
            open={isModal}
            close={() => setIsModal(false)}
            dataDefault={dataRecord}
          />
        )}
      </Spin>
    </div>
  );
};

const DashboradLawyerJobReceiv = MotionHoc(Main);
export default DashboradLawyerJobReceiv;
